import { Editor, Element as SlateElement, Transforms, Node, Point, Range, Path, TextUnit } from 'slate';
import { v4 as uuidv4 } from 'uuid';
import { CustomEditor } from 'slate/types/slate';
import { LIST_TYPES } from './utils/constants';

export const withLists =
  (_options: Record<string, unknown> = {}) =>
  (editor: CustomEditor) => {
    const { insertBreak, normalizeNode, deleteBackward } = editor;

    editor.insertBreak = () => {
      const { selection } = editor;
      if (!selection || !Range.isCollapsed(selection)) {
        return insertBreak();
      }

      const [itemEntry] = Editor.nodes(editor, {
        match: (n) => SlateElement.isElement(n) && n.type === 'list-item',
      });
      if (!itemEntry) {
        return insertBreak();
      }
      const [liNode, liPath] = itemEntry;
      const text = Node.string(liNode);
      const { anchor } = selection;
      const offset = anchor.offset;
      if (text.trim() === '') {
        return Editor.withoutNormalizing(editor, () => {
          Transforms.setNodes(editor, { type: 'paragraph' }, { at: liPath });
          Transforms.unwrapNodes(editor, {
            match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
            split: true,
          });
          const parentPath = Path.parent(liPath);
          const parentNode = Node.get(editor, parentPath);

          const insertIndex = liPath[liPath.length - 1] + 1;
          const insertPath = [...parentPath, insertIndex];
          if (SlateElement.isElement(parentNode) && insertIndex <= parentNode.children.length) {
            Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] }, { at: insertPath });

            Transforms.select(editor, Editor.start(editor, insertPath));
          }
        });
      }

      if (offset > 0 && offset < text.length) {
        return Editor.withoutNormalizing(editor, () => {
          Transforms.splitNodes(editor, {
            at: anchor,
            match: (n) => SlateElement.isElement(n) && n.type === 'list-item',
            always: true,
          });
          const newPath = Path.next(liPath);
          Transforms.setNodes(editor, { type: 'list-item' }, { at: newPath });
          Transforms.select(editor, Editor.start(editor, newPath));
        });
      }
      if (offset === text.length) {
        return Editor.withoutNormalizing(editor, () => {
          const insertPath = Path.next(liPath);
          Transforms.insertNodes(editor, { type: 'list-item', uuid: uuidv4(), children: [{ text: '' }] }, { at: insertPath });
          Transforms.select(editor, Editor.start(editor, insertPath));
        });
      }
      insertBreak();
    };

    editor.normalizeNode = (entry) => {
      const [node, path] = entry;
      if (SlateElement.isElement(node) && LIST_TYPES.includes(node.type)) {
        if (!node.uuid) {
          Transforms.setNodes(editor, { uuid: uuidv4() }, { at: path });
        }
        for (const [childNode, childPath] of Node.children(editor, path)) {
          if (!SlateElement.isElement(childNode) || childNode.type !== 'list-item') {
            Transforms.wrapNodes(editor, { type: 'list-item', uuid: uuidv4(), children: [] }, { at: childPath });
            return;
          }
        }
      }

      if (SlateElement.isElement(node) && node.type === 'list-item') {
        if (!node.uuid) {
          Transforms.setNodes(editor, { uuid: uuidv4() }, { at: path });
        }
        const parent = Node.parent(editor, path);
        if (!parent || !SlateElement.isElement(parent) || !LIST_TYPES.includes(parent.type)) {
          Transforms.setNodes(editor, { type: 'paragraph' }, { at: path });
          return;
        }
      }

      normalizeNode(entry);
    };

    editor.deleteBackward = (unit: TextUnit) => {
      const { selection } = editor;
      if (selection && Range.isCollapsed(selection)) {
        const [liEntry] = Editor.nodes(editor, {
          match: (n) => SlateElement.isElement(n) && n.type === 'list-item',
        });
        if (liEntry) {
          const [liNode, liPath] = liEntry;
          const start = Editor.start(editor, liPath);
          if (Point.equals(selection.anchor, start) && Node.string(liNode).length === 0) {
            Transforms.setNodes(editor, { type: 'paragraph' }, { at: liPath });
            Transforms.unwrapNodes(editor, {
              match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
              split: true,
            });
            return;
          }
        }
      }
      deleteBackward(unit);
    };

    return editor;
  };
